import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import useTranslation from "../../hooks/useTranslation";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { resolveQuickTranslateTargetLanguage } from "../../utils/quickTranslateTargetLanguage";

function detectLanguageStub(text: string): string {
  const lower = text.toLowerCase();
  if (/[äöüß]/.test(lower)) return "GERMAN";
  if (/[éèàçùâêîôûëïüÿ]/.test(lower)) return "FRENCH";
  if (/[áéíóúñü]/.test(lower)) return "SPANISH";
  if (/[àèéìíîòóù]/.test(lower)) return "ITALIAN";
  return "AUTO";
}

function QuickTranslatePage() {
  const { translate } = useTranslation();
  const userSettings = useUserSettings();
  const motherTongue = userSettings.data?.mother_tongue ?? "GERMAN";
  const { languageSpace } = useLanguageSpace("ALL");
  const [text, setText] = useState("");
  const [translated, setTranslated] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const detected = useMemo(() => detectLanguageStub(text), [text]);
  const hasText = Boolean(text.trim());
  const languageLabels: Record<string, string> = {
    AUTO: translate("quick_translate_page_language_auto"),
    ENGLISH: translate("language_english"),
    ESPERANTO: translate("language_esperanto"),
    GERMAN: translate("language_german"),
    FRENCH: translate("language_french"),
    SPANISH: translate("language_spanish"),
    ITALIAN: translate("language_italian"),
  };

  const targetLanguage = resolveQuickTranslateTargetLanguage(
    languageSpace,
    motherTongue,
  );

  const doTranslate = () => {
    if (!text.trim()) return;
    setIsTranslating(true);
    // Spike: simulate translation locally. Wire to /api/translate later.
    setTimeout(() => {
      setTranslated(
        `(Stub) ${text} → ${languageLabels[targetLanguage] || targetLanguage}`,
      );
      setIsTranslating(false);
    }, 250);
  };

  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {translate("quick_translate_page_eyebrow")}
          </p>
          <h1 className="text-3xl font-bold text-slate-900">
            {translate("quick_translate_page_title")}
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            {translate("quick_translate_page_subtitle")}
          </p>
        </header>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
          <div className="flex flex-col gap-3">
            <label className="text-sm font-semibold text-slate-700">
              {translate("quick_translate_page_text_label")}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              placeholder={translate("quick_translate_page_text_placeholder")}
            />
            <p className="text-xs text-slate-500">
              {translate("quick_translate_page_detected_prefix")}{" "}
              {languageLabels[detected] ||
                translate("quick_translate_page_language_auto")}
            </p>
          </div>

          {hasText ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => doTranslate()}
                disabled={isTranslating}
                className="rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isTranslating
                  ? translate("quick_translate_page_translating")
                  : `${translate("quick_translate_page_to_prefix")} ${languageLabels[targetLanguage] || targetLanguage}`}
              </button>
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              {translate("quick_translate_page_empty_hint")}
            </p>
          )}

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 min-h-[80px]">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              {translate("quick_translate_page_translation_label")}
            </p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">
              {translated || translate("quick_translate_page_output_empty")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/quick_translate")({
  component: QuickTranslatePage,
});
